
import React from "react";
import { Link, useLocation } from "react-router-dom";
import { fetchBlogPostBySlug, trackClick as trackClickRequest } from "@/services/d1Service";

interface CtaButtonProps {
  text: string;
  href: string;
  variant?: "primary" | "secondary";
  className?: string;
  ctaType?: string;
}

const CtaButton: React.FC<CtaButtonProps> = ({
  text,
  href,
  variant = "primary",
  className = "",
  ctaType = "magic-letter-box",
}) => {
  const location = useLocation();
  const baseClasses = "inline-block font-semibold py-3 px-6 rounded-xl text-center shadow-md transition-colors duration-300 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-slate-700";
  const variantClasses = {
    primary: "bg-elfRed-ink hover:bg-[#892638] text-white",
    secondary: "bg-elfGreen-ink hover:bg-[#1b422c] text-white",
  };

  const handleTrackClick = async (_event: React.MouseEvent) => {
    try {
      // Extract post ID from URL if we're on a blog post page
      const pathParts = location.pathname.split('/');
      const isBlogPost = pathParts[1] === 'blog' && pathParts.length > 2;
      
      if (!isBlogPost) {
        console.log(`Not tracking click - not on a blog post page. Path: ${location.pathname}`);
        return; // Only track on blog post pages
      }
      
      const slug = pathParts[2];
      console.log(`Tracking click for blog post with slug: ${slug}`);
      
      // Get the post ID from the slug
      const post = await fetchBlogPostBySlug(slug);
      if (!post) {
        console.error(`No post found with slug: ${slug}`);
        return;
      }
      
      console.log(`Found post ID: ${post.id} for slug: ${slug}`);
      
      // Track the click using the edge function
      console.log(`Invoking track-click function with post_id: ${post.id}, cta: ${ctaType}`);
      await trackClickRequest({
        post_id: post.id,
        cta_identifier: ctaType,
      });
      console.log(`CTA click tracked successfully for: ${ctaType} on post: ${post.id}`);
    } catch (error) {
      console.error('Error tracking CTA click:', error);
      // Don't show error toast to users as it doesn't affect their experience
    }
  };

  const isExternal = /^https?:\/\//i.test(href);

  if (isExternal) {
    return (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className={`${baseClasses} ${variantClasses[variant]} ${className}`}
        onClick={handleTrackClick}
        data-cta-type={ctaType}
      >
        {text}
      </a>
    );
  }

  return (
    <Link
      to={href}
      className={`${baseClasses} ${variantClasses[variant]} ${className}`}
      onClick={handleTrackClick}
      data-cta-type={ctaType}
    >
      {text}
    </Link>
  );
};

export default CtaButton;
